import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Loader2, Save, X, Upload, FileText, Trash2, ArrowLeft, Building2, Receipt, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { expenseCategoriesApi, seminarApi } from '../../lib/api';
import api from '../../lib/api';

const formatBytes = (n) => {
    if (!n) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

export default function NewExpensePage() {
    const { expense_id } = useParams();
    const isEdit = !!expense_id;
    const navigate = useNavigate();
    const fileInput = useRef(null);

    const [form, setForm] = useState({
        category_id: '',
        category: '',
        seminar_id: '',
        expense_date: new Date().toISOString().slice(0, 10),
        description: '',
        amount: '',
        recipient: '',
        invoice_number: '',
        bank_account_holder: '',
        bank_name: '',
        bank_iban: '',
        note: '',
    });
    const [paid, setPaid] = useState(false);
    const [approved, setApproved] = useState(false);
    const [belege, setBelege] = useState([]);
    const [categories, setCategories] = useState([]);
    const [seminars, setSeminars] = useState([]);
    const [savedExpenseId, setSavedExpenseId] = useState(expense_id || null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [cats, sems] = await Promise.all([
                    expenseCategoriesApi.list(true),
                    seminarApi.getAll(),
                ]);
                setCategories(cats.data);
                setSeminars(sems.data);
                if (isEdit) {
                    const r = await api.get(`/expenses/${expense_id}`);
                    const e = r.data;
                    setForm({
                        category_id: e.category_id || '',
                        category: e.category || '',
                        seminar_id: e.seminar_id || '',
                        expense_date: e.expense_date || new Date().toISOString().slice(0, 10),
                        description: e.description || '',
                        amount: e.amount || '',
                        recipient: e.recipient || '',
                        invoice_number: e.invoice_number || '',
                        bank_account_holder: e.bank_account_holder || '',
                        bank_name: e.bank_name || '',
                        bank_iban: e.bank_iban || '',
                        note: e.note || '',
                    });
                    setPaid(!!e.paid);
                    setApproved(!!e.approved);
                    setBelege(e.belege || []);
                }
            } catch {
                toast.error('Daten konnten nicht geladen werden');
            } finally {
                setLoading(false);
            }
        })();
    }, [expense_id, isEdit]);

    const handleCategoryChange = (catId) => {
        const cat = categories.find(c => c.id === catId);
        setForm(f => ({ ...f, category_id: catId, category: cat?.name || '' }));
    };

    const handleSave = async () => {
        if (!form.category_id) { toast.error('Bitte Kategorie wählen'); return; }
        if (!form.description.trim()) { toast.error('Bitte Bezeichnung angeben'); return; }
        if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Bitte gültigen Betrag angeben'); return; }
        setSaving(true);
        try {
            const payload = { ...form, amount: parseFloat(form.amount) };
            let id = savedExpenseId;
            if (id) {
                await api.put(`/expenses/${id}`, payload);
            } else {
                const r = await api.post('/expenses', payload);
                id = r.data.id;
                setSavedExpenseId(id);
            }
            // Synchronisiere Status (paid/approved) – beide werden via PUT /pay bzw. /approve gesetzt
            if (id) {
                if (paid) {
                    try { await api.put(`/expenses/${id}/pay`, { payment_date: form.expense_date }); } catch { /* ignore */ }
                }
                try { await api.put(`/expenses/${id}/approve`, { approved }); } catch { /* ignore */ }
            }
            toast.success(isEdit ? 'Ausgabe aktualisiert' : 'Ausgabe gespeichert');
            navigate('/admin/invoices?tab=expenses');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    const handleFile = async (file) => {
        if (!file) return;
        if (file.type !== 'application/pdf') { toast.error('Nur PDF-Dateien erlaubt'); return; }
        if (file.size > 10 * 1024 * 1024) { toast.error('Datei größer als 10 MB'); return; }
        // Entry zuerst speichern, falls noch nicht persistiert
        let id = savedExpenseId;
        if (!id) {
            if (!form.category_id || !form.description.trim() || !form.amount) {
                toast.error('Bitte zuerst Kategorie, Bezeichnung und Betrag eingeben, dann Beleg hochladen');
                return;
            }
            try {
                const r = await api.post('/expenses', { ...form, amount: parseFloat(form.amount) });
                id = r.data.id;
                setSavedExpenseId(id);
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Konnte Ausgabe nicht anlegen');
                return;
            }
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post(`/expenses/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setBelege(r.data.belege || []);
            toast.success('Beleg hochgeladen' + (r.data.belege?.[r.data.belege.length - 1]?.dropbox_path ? ' (Dropbox synchronisiert)' : ''));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteBeleg = async (belegId) => {
        if (!savedExpenseId) return;
        if (!window.confirm('Beleg wirklich entfernen?')) return;
        try {
            await api.delete(`/expenses/${savedExpenseId}/belege/${belegId}`);
            setBelege(belege.filter(b => b.id !== belegId));
            toast.success('Beleg entfernt');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="expense-form-page">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/invoices?tab=expenses')} data-testid="expense-back-btn">
                            <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold font-heading">{isEdit ? 'Ausgabe bearbeiten' : 'Ausgabe hinzufügen'}</h1>
                            <p className="text-sm text-muted-foreground">Erfassen Sie eine neue Ausgabe und laden Sie zugehörige Belege hoch.</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => navigate('/admin/invoices?tab=expenses')} data-testid="expense-cancel-btn">
                            <X className="h-4 w-4 mr-1" /> Abbrechen
                        </Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="expense-save-btn">
                            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Speichern
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* LINKS: 2 Spalten breit */}
                    <div className="lg:col-span-2 space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base font-heading flex items-center gap-2">
                                    <Receipt className="h-4 w-4" /> Ausgabendaten
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label>Kategorie *</Label>
                                        <Select value={form.category_id} onValueChange={handleCategoryChange}>
                                            <SelectTrigger data-testid="expense-category"><SelectValue placeholder="Kategorie wählen" /></SelectTrigger>
                                            <SelectContent>
                                                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Datum *</Label>
                                        <Input type="date" value={form.expense_date} onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))} data-testid="expense-date" />
                                    </div>
                                </div>
                                <div>
                                    <Label>Bezeichnung *</Label>
                                    <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="z. B. Tagungsraum Mai 2026" data-testid="expense-description" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label>Betrag (€) *</Label>
                                        <Input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="expense-amount" />
                                    </div>
                                    <div>
                                        <Label>Lieferant / Empfänger</Label>
                                        <Input value={form.recipient} onChange={e => setForm(f => ({ ...f, recipient: e.target.value }))} placeholder="Firma oder Person" data-testid="expense-recipient" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label>Rechnungsnummer</Label>
                                        <Input value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} data-testid="expense-invoice-number" />
                                    </div>
                                    <div>
                                        <Label>Seminar (optional)</Label>
                                        <Select value={form.seminar_id || 'none'} onValueChange={v => setForm(f => ({ ...f, seminar_id: v === 'none' ? '' : v }))}>
                                            <SelectTrigger data-testid="expense-seminar"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Keinem Seminar zuordnen</SelectItem>
                                                {seminars.map(s => (
                                                    <SelectItem key={s.id} value={s.id}>
                                                        {s.course_number ? `${s.course_number} · ` : ''}{s.title}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Bankdaten */}
                        <Card className="border-blue-200 bg-blue-50/50">
                            <CardHeader>
                                <CardTitle className="text-base font-heading flex items-center gap-2 text-blue-900">
                                    <Building2 className="h-4 w-4" /> Bankdaten des Empfängers
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label>Kontoinhaber</Label>
                                    <Input value={form.bank_account_holder} onChange={e => setForm(f => ({ ...f, bank_account_holder: e.target.value }))} data-testid="expense-bank-holder" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label>Bank</Label>
                                        <Input value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} data-testid="expense-bank-name" />
                                    </div>
                                    <div>
                                        <Label>IBAN</Label>
                                        <Input value={form.bank_iban} onChange={e => setForm(f => ({ ...f, bank_iban: e.target.value.toUpperCase() }))} placeholder="DE…" data-testid="expense-bank-iban" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-5 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label className="text-sm font-medium">Zahlungsstatus</Label>
                                        <p className="text-xs text-muted-foreground">Markiert die Ausgabe als bereits bezahlt</p>
                                    </div>
                                    <Switch checked={paid} onCheckedChange={setPaid} data-testid="expense-paid-toggle" />
                                </div>
                                <div>
                                    <Label>Notiz (intern)</Label>
                                    <Textarea rows={3} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Zusätzliche Hinweise zur Ausgabe…" data-testid="expense-note" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* RECHTS: Belege + Status */}
                    <div className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base font-heading flex items-center gap-2">
                                    <FileText className="h-4 w-4" /> Belege
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <input
                                    ref={fileInput}
                                    type="file"
                                    accept="application/pdf"
                                    className="hidden"
                                    onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
                                    data-testid="expense-file-input"
                                />
                                <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => fileInput.current?.click()}
                                    disabled={uploading}
                                    data-testid="expense-upload-btn"
                                >
                                    {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                                    Belege auswählen
                                </Button>
                                <p className="text-xs text-muted-foreground text-center">PDF, max. 10 MB pro Datei. Wird in Dropbox unter <code className="bg-muted px-1 rounded">/BGPR/Ausgaben/&lt;Seminar-Nr.&gt;/</code> abgelegt.</p>

                                {belege.length === 0 ? (
                                    <p className="text-xs text-muted-foreground italic text-center py-3">Noch keine Belege hochgeladen.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {belege.map(b => (
                                            <div key={b.id} className="flex items-center gap-2 p-2 border rounded-md bg-muted/30 text-xs" data-testid={`beleg-${b.id}`}>
                                                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate font-medium">{b.filename}</div>
                                                    <div className="text-muted-foreground text-[10px]">
                                                        {formatBytes(b.size)}
                                                        {b.dropbox_path && <span className="ml-1">· Dropbox ✓</span>}
                                                    </div>
                                                </div>
                                                {b.storage_url && (
                                                    <a href={b.storage_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Öffnen</a>
                                                )}
                                                <button onClick={() => handleDeleteBeleg(b.id)} className="text-red-600 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base font-heading flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4" /> Status
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 text-sm">
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Zahlungsstatus</span>
                                    <Badge className={paid ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                                        {paid ? 'Bezahlt' : 'Offen'}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Freigabe</span>
                                    <Switch checked={approved} onCheckedChange={setApproved} data-testid="expense-approve-toggle" />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Belege</span>
                                    <Badge variant="outline">{belege.length} Datei{belege.length === 1 ? '' : 'en'}</Badge>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
